import { forwardRef } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, FOCUS_RING, RADIUS_CONTROL, STATUS_FIELD } from './internal/presets'

const clamp = (value, min, max) => {
  if (value == null || value === '') return value
  let next = Number(value)
  if (Number.isNaN(next)) return null
  if (min != null) next = Math.max(next, min)
  if (max != null) next = Math.min(next, max)
  return next
}

/**
 * antd InputNumber. Reports a number (or null) to `onChange`, not an event, and
 * applies `formatter`/`parser`, `min`/`max`, `step` and `precision`.
 */
const InputNumber = forwardRef(
  (
    {
      value,
      defaultValue,
      onChange,
      onBlur,
      onPressEnter,
      min,
      max,
      step = 1,
      precision,
      formatter,
      parser,
      disabled = false,
      readOnly = false,
      status,
      size = 'middle',
      controls = true,
      addonAfter,
      placeholder,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useControllableValue(value, defaultValue ?? null)

    const commit = (next) => {
      const clamped = clamp(next, min, max)
      const rounded =
        clamped != null && precision != null ? Number(Number(clamped).toFixed(precision)) : clamped
      setInnerValue(rounded)
      onChange?.(rounded)
    }

    const handleChange = (event) => {
      const raw = event.target.value
      if (raw === '' || raw === '-') {
        setInnerValue(raw === '' ? null : raw)
        onChange?.(null)
        return
      }
      const parsed = parser ? parser(raw) : raw.replace(/[^\d.,-]/g, '').replace(',', '.')
      const numeric = Number(parsed)
      if (Number.isNaN(numeric)) return
      setInnerValue(numeric)
      onChange?.(numeric)
    }

    const bump = (direction) => {
      const base = Number(innerValue) || 0
      commit(base + direction * Number(step))
    }

    const displayValue =
      innerValue == null
        ? ''
        : formatter
          ? formatter(innerValue)
          : String(innerValue)

    const control = (
      <span
        className={cn(
          'ui-input-number ui-input-base flex items-center gap-1 p-1.5',
          RADIUS_CONTROL,
          STATUS_FIELD[status],
          disabled && 'cursor-not-allowed bg-[var(--gray-100)]',
          'focus-within:ring-2 focus-within:ring-[var(--primary-color)] focus-within:ring-offset-1',
          size === 'small'
            ? 'min-h-[var(--control-height-sm)]'
            : 'min-h-[var(--control-height)]',
          className
        )}
        style={style}
      >
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          role="spinbutton"
          aria-valuenow={typeof innerValue === 'number' ? innerValue : undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-invalid={status === 'error' || undefined}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onBlur={(event) => {
            commit(innerValue)
            onBlur?.(event)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onPressEnter?.(event)
            if (event.key === 'ArrowUp') {
              event.preventDefault()
              bump(1)
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              bump(-1)
            }
          }}
          className="ui-input-number-input ui-numeric min-w-0 flex-1 border-0 bg-transparent px-2 text-sm outline-none"
          {...rest}
        />
        {controls && !disabled && !readOnly ? (
          <span className="ui-input-number-handler-wrap flex flex-col">
            <button
              type="button"
              tabIndex={-1}
              aria-label="Augmenter"
              onClick={() => bump(1)}
              className="ui-input-number-handler-up flex h-4 w-5 items-center justify-center text-[var(--gray-600)] hover:text-[var(--primary-color)]"
            >
              <ChevronUp size={12} aria-hidden="true" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-label="Diminuer"
              onClick={() => bump(-1)}
              className="ui-input-number-handler-down flex h-4 w-5 items-center justify-center text-[var(--gray-600)] hover:text-[var(--primary-color)]"
            >
              <ChevronDown size={12} aria-hidden="true" />
            </button>
          </span>
        ) : null}
      </span>
    )

    if (!addonAfter) return control

    return (
      <span className="ui-input-number-group-wrapper flex items-stretch">
        <span className="min-w-0 flex-1">{control}</span>
        <span className={cn('ui-input-number-group-addon flex items-center border border-l-0 bg-[var(--gray-100)] px-3 text-sm', BORDER)}>
          {addonAfter}
        </span>
      </span>
    )
  }
)

InputNumber.displayName = 'InputNumber'

export default InputNumber
